/** tm/legal/LegalItemDetail.tsx — 법령·판례·해석 상세 (GET /items/:id): 메타·질의요지·요지·전문 펼치기·링크·별표·안 읽음·메모·업체 연결 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { C, T } from '../components/ui';
import { getItem, setItemRead, setItemStar, saveItemTitle } from './legalApi';
import { btn, shortSource, shortDate, effectiveLabel, lawUrl, safeHttp, errMsg, lawKindLabel, AMBER, MUTED } from './legalTheme';
import { DetailSkeleton, DetailHead, MetaGrid, Box, Collapsible, DetailEmpty, actsRow } from './LegalDetailParts';
import LegalMemoLinks from './LegalMemoLinks';
import LegalStageTimeline from './LegalStageTimeline';
import { TitleEditButton, TitleEditor } from './LegalTitleEdit';
import { OriginalTitleLine, MineTitle } from './legalUi';
import type { RowPatch } from './useLegalFeed';
import type { PickCompany } from './CompanyPicker';
import type { LegalItemDetail as Item, LinkedCompany, Selection } from './legalTypes';

interface Props {
  id: number; mobile: boolean; companies: PickCompany[] | null; // null = 업체 연결 숨김(올댓벤처스)
  onBack: () => void; onToast: (m: string, k?: 'ok' | 'error') => void;
  onPatch: (p: RowPatch) => void;
  onOpen?: (s: NonNullable<Selection>) => void;
}

const MAIN_KEYS = ['요지', '판결요지', '재결요지', '결정요지', '회답', '판시사항', '주문'];
/** 따로 펼쳐 보여 주는 칸 (전문 펼치기에서 빼서 두 번 나오지 않게) */
const HIDE_KEYS = ['질의요지', '제개정이유', '회신'];
/** 요지가 없을 때 — 서버 detail_note → 출처별 문구 */
function noSummary(it: Item): string {
  if (it.detail_status === 'pending') return '본문을 아직 받지 못했습니다. 잠시 뒤 다시 열어 주세요.';
  if (it.detail_note === '목록만 제공') return '이 출처는 목록만 제공합니다. 원문 보기에서 전문을 확인하세요.';
  if (it.group === '판례') return '본문을 아직 받지 못했습니다. 원문 보기에서 판시사항·판결요지를 확인하세요.';
  return '요지가 따로 없습니다. 원문 보기에서 전문을 확인하세요.';
}

function dateLabel(it: Item) {
  if (it.group === '판례') return '선고일';
  if (it.group === '심판례') return '의결일';
  return '해석일';
}

export default function LegalItemDetail({ id, mobile, companies, onBack, onToast, onPatch, onOpen }: Props) {
  const [it, setIt] = useState<Item | null>(null);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(false);
  useEffect(() => { setEditing(false); }, [id]);
  const load = useCallback(async () => {
    setIt(null); setErr('');
    try { setIt(await getItem(id)); } catch (e) { setErr(errMsg(e, '본문을 불러오지 못했습니다')); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (err) return <>{mobile && <button type="button" className="lw-f" onClick={onBack} style={btn()}>← 목록</button>}<DetailEmpty error={err} onRetry={load} /></>;
  if (!it) return <DetailSkeleton />;

  const isLaw = it.source === 'law' || it.tab === 'law';
  const d = it.detail || {};
  const mainKey = MAIN_KEYS.find(k => d[k]);
  const used = new Set([...HIDE_KEYS, ...(mainKey ? [mainKey] : [])]);
  const rest = Object.entries(d).filter(([k, v]) => !used.has(k) && !!v);
  const kind = it.kind_label || it.kind || '';
  const eff = effectiveLabel(it.effective_at);
  const topics = it.topics || it.keywords || [];
  const sibs = it.siblings || [];
  const promNo = (it.meta?.promulgationNo || it.case_no || '').replace(/^제|호$/g, '');

  // 내 제목 — 화면(상세·목록 카드) 먼저 바꾸고 실패하면 되돌린다
  const saveTitle = async (next: string | null) => {
    setEditing(false);
    const prev = it.custom_title ?? null;
    if (next === prev) return;
    const put = (v: string | null) => { setIt(cur => (cur && cur.id === it.id ? { ...cur, custom_title: v } : cur)); onPatch({ custom_title: v }); };
    put(next);
    try {
      const r = await saveItemTitle(it.id, next ?? '');
      const saved = r ? r.custom_title : next;
      if (saved !== next) put(saved);
      onToast(saved ? '제목을 바꿨습니다' : '원래 제목으로 되돌렸습니다');
    } catch (e) { put(prev); onToast(errMsg(e, '제목을 저장하지 못했습니다'), 'error'); }
  };

  const star = async () => {
    const next = !it.starred;
    setIt({ ...it, starred: next }); onPatch({ starred: next });
    try { await setItemStar(it.id, next); onToast(next ? '별표했습니다' : '별표를 해제했습니다'); }
    catch (e) { setIt({ ...it, starred: !next }); onPatch({ starred: !next }); onToast(errMsg(e, '별표하지 못했습니다'), 'error'); }
  };
  const unread = async () => {
    onPatch({ unread: true });
    try { await setItemRead(it.id, false); onToast('안 읽음으로 바꿨습니다'); }
    catch (e) { onPatch({ unread: false }); onToast(errMsg(e, '바꾸지 못했습니다'), 'error'); }
  };
  const copy = (no: string, label: string) => {
    if (!navigator.clipboard) { onToast('복사하지 못했습니다. 번호를 직접 선택해 주세요', 'error'); return; }
    navigator.clipboard.writeText(no).then(() => onToast(`${label} 복사: ${no}`), () => onToast('복사하지 못했습니다. 번호를 직접 선택해 주세요', 'error'));
  };
  // 국세청 해석 번호 "서면-2026-소득-1058[소득세과-1136]" → 문서번호 · 회신번호 따로
  const nos = (it.case_no || '').match(/^(.*?)\s*\[(.+)\]\s*$/);

  const meta: [string, ReactNode][] = isLaw ? [
    ['구분', `${lawKindLabel(it.meta?.lawType) ? `${lawKindLabel(it.meta?.lawType)} · ` : ''}${kind === '제정' ? '신설(제정)' : kind || '개정'}${it.meta?.status ? ` (${it.meta.status})` : eff ? ` (${eff.upcoming ? '시행예정' : '시행 중'})` : ''}`],
    ['공포', `${shortDate(it.decided_at)}${promNo ? ` · 제${promNo}호` : ''}`],
    ['시행', <>{shortDate(it.effective_at)} {eff && <b style={{ color: eff.upcoming ? C.accent : C.sub, fontSize: T.sm }}>{eff.text}</b>}</>],
    ['소관', it.org || '-'],
  ] : [
    ['사건·안건번호', it.case_no || '-'], ['기관', it.org || it.source_label],
    ...(it.requester ? [['질의기관', it.requester] as [string, string]] : []),
    [dateLabel(it), <>{it.decided_at ? shortDate(it.decided_at) : '일자 미상'}{it.date_note && <span style={{ marginLeft: 8, fontSize: T.md, color: '#B45309' }}>({it.date_note})</span>}</>],
  ];
  const lawName = it.title;
  const link = (href: string | null | undefined, label: string, pri = false) => {
    const u = safeHttp(href);
    return u ? <a key={label} className="lw-f" href={u} target="_blank" rel="noopener noreferrer" style={btn(pri ? 'pri' : 'sec')}>{label}</a> : null;
  };

  return (
    <div>
      <DetailHead group={it.group} label={shortSource(it.source, it.source_label)} topics={topics} mobile={mobile} onBack={onBack} stage={isLaw ? 'law' : undefined}
        title={it.custom_title ? <MineTitle text={it.custom_title} /> : it.title} titleTools={<TitleEditButton onClick={() => setEditing(true)} />}
        subTitle={it.custom_title ? <OriginalTitleLine title={it.title} /> : undefined}
        editor={editing ? <TitleEditor original={it.title} custom={it.custom_title} mobile={mobile} onSave={saveTitle} onCancel={() => setEditing(false)} /> : undefined}
        extra={it.meta?.minor ? <span style={{ fontSize: T.sm, color: MUTED, border: `1px solid ${C.border}`, borderRadius: 4, padding: '0 5px' }}>타법개정</span> : undefined} />
      <MetaGrid rows={meta} mobile={mobile} />
      {sibs.length > 1 && (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 14px', marginBottom: 12, maxWidth: 760 }}>
          <h4 style={{ margin: '0 0 6px', fontSize: T.md, color: C.sub }}>같은 내용 {sibs.length}건 <span style={{ fontWeight: 400, color: MUTED }}>— 같은 회신이 번호만 달리 여러 번 실렸습니다. 새 소식은 1건으로 셉니다</span></h4>
          {sibs.map(x => (
            <div key={x.id} style={{ display: 'flex', gap: 10, fontSize: 14, padding: '4px 0', borderTop: `1px solid ${C.bg}`, fontVariantNumeric: 'tabular-nums' }}>
              <span style={{ flex: 1 }}>{x.case_no || '-'}{x.id === it.id ? ' (지금 보는 글)' : ''}</span>
              <span style={{ color: C.sub }}>{x.decided_at ? shortDate(x.decided_at) : '일자 미상'}</span>
              {safeHttp(x.public_url) && <a className="lw-f" href={safeHttp(x.public_url) || undefined} target="_blank" rel="noopener noreferrer" style={{ color: C.accent }}>원문</a>}
            </div>
          ))}
        </div>
      )}
      {d['질의요지'] && <Box kind="q" title="질의요지">{d['질의요지']}</Box>}
      {isLaw ? (
        (d['제개정이유'] || it.summary) ? <Box title="제개정 이유">{d['제개정이유'] || it.summary}</Box>
          : <Box kind="q" title="제개정 이유">{it.detail_status === 'pending' ? '본문을 아직 받지 못했습니다. 잠시 후 다시 열어 주세요.' : '법제처가 제개정 이유를 제공하지 않았습니다. 신구법 비교에서 바뀐 조문을 확인하세요.'}</Box>
      ) : mainKey ? <Box title={mainKey}>{d[mainKey]}</Box>
        : it.summary ? <Box title="요지">{it.summary}</Box>
          : <Box kind="q" title="요지">{noSummary(it)}</Box>}
      {!isLaw && d['회신'] && <Box kind="q" title="회신">{d['회신']}</Box>}
      {isLaw && <LegalStageTimeline kind="legal" id={it.id} onOpen={onOpen} />}
      <Collapsible sections={rest} />
      <div style={actsRow}>
        {isLaw ? <>
          {link(it.meta?.compare_url || it.public_url, '신구법 비교', true)}
          {link(it.meta?.revision_url, '개정문')}
          {link(lawUrl(lawName), '현행 법령')}
        </> : <>
          {link(it.public_url, '원문 보기', true)}
          {nos ? <>
            <button type="button" className="lw-f" onClick={() => copy(nos[1], '문서번호')} title={nos[1]} style={btn()}>문서번호 복사</button>
            <button type="button" className="lw-f" onClick={() => copy(nos[2], '회신번호')} title={nos[2]} style={btn()}>회신번호 복사</button>
          </> : it.case_no && <button type="button" className="lw-f" onClick={() => copy(it.case_no || '', '번호')} style={btn()}>번호 복사</button>}
        </>}
        <button type="button" className="lw-f" onClick={star} aria-pressed={it.starred} style={{ ...btn(), color: it.starred ? AMBER : C.sub }}>{it.starred ? '★ 별표 해제' : '☆ 별표'}</button>
        <button type="button" className="lw-f" onClick={unread} style={btn()}>안 읽음으로</button>
      </div>
      <LegalMemoLinks itemId={it.id} memo={it.memo || ''} linked={it.companies || []} companies={companies} files={it.files} onToast={onToast}
        onLinked={(list: LinkedCompany[]) => setIt(cur => (cur ? { ...cur, companies: list } : cur))} />
    </div>
  );
}
